import {
	ErrorModal,
	MesfModal,
	useUTLSettingsContext,
} from "@dexteel/mesf-core";
import {
	Box,
	Checkbox,
	DialogProps,
	FormControlLabel,
	Grid2,
	TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { DelayCategorySelector } from "@/shared/selectors/DelayCategorySelector";
import { DelayCodeTreePicker } from "@/shared/selectors/DelayCodeTreePicker";
import { HelmetDexteel } from "@dexteel/mesf-core";
import { capitalize, get } from "lodash-es";
import { useConfigurationDelayCodeContext } from "../../context/ConfigurationDelayCodeContext";
import { useDelayCodes } from "../../hooks/delays/useDelayCodes";
import {
	CATALOG_DELAY_INITIAL_VALUES,
	CatalogDelayCode,
} from "../../models/CatalogDelayCode";
import {
	deleteDelay,
	getDelay,
	upsertDelay,
} from "../../repositories/ConfigurationDelayRepository";

type Props = {
	DelayId: number | null | undefined;
	modal: "new" | "edit" | "delete" | "view" | "";
	onHide: (shouldUpdate: boolean) => void;
	assetId: number | null;
};

export const UpsertDeleteViewDelay = ({
	DelayId,
	modal,
	onHide,
	assetId,
}: Props) => {
	const { state } = useUTLSettingsContext();
	const undeclaredId = Number(
		get(state, `settings.DELAYS_UNDECLARED_MILL`, 11),
	);
	const undeclaredColdShearId = Number(
		get(state, `settings.DELAYS_UNDECLARED_COLD_SHEAR`, 12),
	);

	const maxWidth: DialogProps["maxWidth"] = "sm";
	const [error, setError] = useState<string>("");
	const [title, setTitle] = useState<string>("CREATE");
	const [notificationMessage, setNotificationMessage] = useState<string>("");
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const succesfullyMessage = `The delay code was ${modal}d succesfully`;

	const {
		state: { delayAreaAsset, delayNodeSelectedInTree, allDelayCodes },
	} = useConfigurationDelayCodeContext();

	const { loadFilterDelays } = useDelayCodes();

	const { control, reset, setValue, handleSubmit, getValues } =
		useForm<CatalogDelayCode>({
			defaultValues: delayNodeSelectedInTree
				? delayNodeSelectedInTree
				: CATALOG_DELAY_INITIAL_VALUES,
		});

	const handleCancel = () => {
		onHide(false);
	};

	const onSubmit: SubmitHandler<CatalogDelayCode> = async (
		data: CatalogDelayCode,
	) => {
		setIsSubmitLoading(true);
		if (modal === "delete") {
			const delayResp = await deleteDelay(
				delayNodeSelectedInTree.DelayCodeId as number,
			);
			if (delayResp.ok) {
				setNotificationMessage(succesfullyMessage);
				onHide(true);
			} else {
				setError(delayResp.message);
			}
		} else {
			const delayResp = await upsertDelay(data);
			if (delayResp.ok) {
				setNotificationMessage(succesfullyMessage);
				onHide(true);
			} else {
				setError(delayResp.message);
			}
		}
		setIsSubmitLoading(false);
	};

	useEffect(() => {
		if (modal !== "") {
			reset();
			loadFilterDelays();
			setTitle(
				modal === "new"
					? "CREATE"
					: modal === "edit"
						? "EDIT"
						: modal === "delete"
							? "DELETE"
							: "VIEW",
			);
			if (modal !== "new") {
				(async () => {
					setIsLoading(true);
					const response = await getDelay(DelayId!);
					if (response.ok) {
						const delayCode = get(
							response,
							"data.tables[0].rows[0]",
							null,
						) as CatalogDelayCode;
						if (delayCode) {
							setValue("ParentDelayCodeId", delayCode.ParentDelayCodeId);
							setValue("DelayAreaAssetId", delayCode.DelayAreaAssetId);
							setValue("DelayCodeId", delayCode.DelayCodeId);
							setValue("DelayCodeName", delayCode.DelayCodeName);
							setValue("DelayCategoryId", delayCode.DelayCategoryId);
							setValue("IsBranch", delayCode?.IsBranch);
							setValue("IsSelectable", delayCode?.IsSelectable);
							setValue("IsEditable", delayCode?.IsEditable);
						}
					} else {
						setError(response.message);
						return;
					}
					setIsLoading(false);
				})();
			} else {
				// If there is a node selected, we take it as parent (only if its editable)
				if (
					delayNodeSelectedInTree?.ParentDelayCodeId &&
					delayNodeSelectedInTree?.IsEditable
				) {
					setValue("ParentDelayCodeId", delayNodeSelectedInTree?.DelayCodeId);
					setValue("DelayCategoryId", delayNodeSelectedInTree?.DelayCategoryId);
				} else {
					const parentRoot: CatalogDelayCode = allDelayCodes.find(
						(node: CatalogDelayCode) => node.ParentDelayCodeId === null,
					);
					if (parentRoot) {
						setValue("ParentDelayCodeId", parentRoot.DelayCodeId);
						setValue("DelayCategoryId", null);
					}
				}
				setValue("DelayCodeName", "");
				setValue("DelayAreaAssetId", delayAreaAsset?.AssetId!);
			}
		}
	}, [modal]);

	return (
		<>
			{(modal === "new" ||
				modal === "edit" ||
				modal === "delete" ||
				modal === "view") && (
				<HelmetDexteel
					title={`${capitalize(modal.toLowerCase())} Delay Code - Delay Codes`}
				/>
			)}
			<Box
				sx={{
					"& .input-group-text": {
						display: "none",
					},
					"& .MuiDialogContent-dividers": {
						padding: 0,
					},
				}}
			>
				<MesfModal
					title={`${title} DELAY CODE`}
					open={modal !== ""}
					maxWidth={maxWidth}
					handleClose={() => handleCancel()}
				>
					<form onSubmit={handleSubmit(onSubmit)}>
						<MesfModal.Content dividers>
							{isLoading ? (
								<CenteredLazyLoading />
							) : (
								<>
									<Grid2 container spacing={1}>
										<Grid2 size={{ xs: 10, sm: 12, md: 3 }}>
											<Controller
												name="DelayAreaAssetId"
												control={control}
												render={() => (
													<>
														<TextField
															label="Asset"
															variant="outlined"
															value={delayAreaAsset?.AssetName}
															disabled
															margin="dense"
															size="small"
															fullWidth
														/>
													</>
												)}
											/>
										</Grid2>
										<Grid2 size={{ xs: 10, sm: 12, md: 9 }}>
											<Controller
												name="DelayCodeName"
												control={control}
												rules={{ required: "Name is required" }}
												render={({
													field: { onChange, value },
													fieldState: { error },
												}) => (
													<TextField
														required
														label="Delay Code Name"
														variant="outlined"
														fullWidth
														margin="dense"
														autoComplete="off"
														onChange={onChange}
														value={value}
														disabled={modal === "delete" || modal === "view"}
														error={!!error}
														helperText={error?.message}
													/>
												)}
											/>
										</Grid2>
									</Grid2>
									<Grid2 container spacing={1}>
										{(delayNodeSelectedInTree?.ParentDelayCodeId ||
											modal === "new") && (
											<Grid2 size={{ md: 12, xs: 12 }}>
												<Controller
													name="ParentDelayCodeId"
													rules={{
														validate: (value) => {
															if (
																value === undeclaredId ||
																value === undeclaredColdShearId
															) {
																return "Undeclared cannot be used as a Parent";
															}
															return true;
														},
													}}
													control={control}
													render={({ field, fieldState: { error } }) => (
														<DelayCodeTreePicker
															label="Parent"
															assetId={getValues("DelayAreaAssetId")}
															value={field.value}
															onChange={field.onChange}
															fieldError={error}
														/>
													)}
												/>
											</Grid2>
										)}
										<Grid2 size={{ md: 12, xs: 12 }}>
											<Controller
												name="DelayCategoryId"
												control={control}
												rules={{ required: "Category is required" }}
												render={({ field, fieldState: { error } }) => (
													<DelayCategorySelector
														label="Category"
														value={field.value}
														onChange={field.onChange}
														fieldError={error}
														disabled={modal === "delete" || modal === "view"}
														assetId={assetId}
														required
													/>
												)}
											/>
										</Grid2>
										<Grid2 size={{ xs: 6, md: 6 }}>
											<Controller
												name="IsBranch"
												control={control}
												defaultValue={false}
												render={({ field }) => (
													<FormControlLabel
														sx={{
															userSelect: "none",
														}}
														control={
															<Checkbox
																checked={field.value as boolean}
																onChange={(e) =>
																	field.onChange(e.target.checked)
																}
																name="IsAsset"
																color="primary"
																disabled
															/>
														}
														label="Has Children"
													/>
												)}
											/>
										</Grid2>
									</Grid2>
								</>
							)}
						</MesfModal.Content>
						<MesfModal.Actions>
							<FooterModalButtons
								isSubmitLoading={isSubmitLoading}
								onHide={handleCancel}
								buttonLabel={modal === "delete" ? "Delete" : "Save"}
								buttonColor={modal === "delete" ? "secondary" : "primary"}
							/>
						</MesfModal.Actions>
					</form>
				</MesfModal>
				<ErrorModal error={error} onHide={() => setError("")} />
				<NotificationSnackBar
					message={notificationMessage}
					onHide={() => setNotificationMessage("")}
				/>
			</Box>
		</>
	);
};
